import { StrictMode } from "react"
import { createRoot, type Root } from "react-dom/client"
import { ErrorBoundary } from "@/components/ErrorBoundary"
import { FeedRunAnalysisPanel } from "@/components/FeedRunAnalysisPanel"
import { FeedAgentAssignments } from "@/components/FeedAgentAssignments"
import { FeedRunWorkflow, type FeedWorkflowPayload } from "@/components/feeds/FeedRunWorkflow"
import { captureEvent } from "@/lib/analytics"
import type { FeedAssignmentPayload, FeedRunAnalysisPayload } from "@/types/researchAgents"

let runRoot: Root | null = null
let feedRoot: Root | null = null
let workflowRoot: Root | null = null

function unmount(): void {
  runRoot?.unmount()
  feedRoot?.unmount()
  workflowRoot?.unmount()
  runRoot = null
  feedRoot = null
  workflowRoot = null
}

function mount(): void {
  unmount()

  const workflowEl = document.getElementById("react-feed-run-workflow")
  if (workflowEl) {
    try {
      const payload = JSON.parse(workflowEl.dataset.payload ?? "") as FeedWorkflowPayload
      workflowRoot = createRoot(workflowEl)
      captureEvent("feed_run_workflow_viewed", {
        feed_type: payload.feed_type,
        feed_run_id: payload.feed_run_id,
        step_count: payload.steps.length,
      })
      workflowRoot.render(
        <StrictMode>
          <ErrorBoundary>
            <FeedRunWorkflow payload={payload} />
          </ErrorBoundary>
        </StrictMode>,
      )
    } catch {
      // ignore malformed payload
    }
  }

  const runEl = document.getElementById("react-feed-run-analysis")
  if (runEl) {
    try {
      const payload = JSON.parse(runEl.dataset.payload ?? "") as FeedRunAnalysisPayload
      runRoot = createRoot(runEl)
      runRoot.render(
        <StrictMode>
          <ErrorBoundary>
            <FeedRunAnalysisPanel payload={payload} />
          </ErrorBoundary>
        </StrictMode>
      )
    } catch {
      // ignore malformed payload
    }
  }

  const feedEl = document.getElementById("react-feed-agent-assignments")
  if (feedEl) {
    try {
      const payload = JSON.parse(feedEl.dataset.payload ?? "") as FeedAssignmentPayload
      feedRoot = createRoot(feedEl)
      feedRoot.render(
        <StrictMode>
          <ErrorBoundary>
            <FeedAgentAssignments payload={payload} />
          </ErrorBoundary>
        </StrictMode>
      )
    } catch {
      // ignore malformed payload
    }
  }
}

document.addEventListener("turbo:load", mount)
document.addEventListener("turbo:before-cache", unmount)
mount()
